import { ArrowLongIcon } from '@common_components/icons/arrowLong.icon';
import Button from '@common_components/buttons/button.component';
import React, { useState } from 'react';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: ( page: number ) => void;
}

const Pagination: React.FC<PaginationProps> = ( { currentPage, totalPages, onPageChange } ) => {
  const [ inputPage, setInputPage ] = useState<number>( currentPage );

  const handlePrevPage = () => {
    if ( currentPage > 1 ) {onPageChange( currentPage - 1 );}
  };

  const handleNextPage = () => {
    if ( currentPage < totalPages ) {onPageChange( currentPage + 1 );}
  };

  const handlePageInputChange = ( e: React.ChangeEvent<HTMLInputElement> ) => {
    const val = parseInt( e.target.value );
    if ( !isNaN( val ) ) {
      const value = Math.max( 1, Math.min( val, totalPages ) );
      setInputPage( value );
    }
  };

  const handleKeyDown = ( e: React.KeyboardEvent<HTMLInputElement> ) => {
    if ( e.key === 'Enter' ) {
      if ( inputPage !== currentPage ) {
        onPageChange( inputPage );
      }
    }
  };

  const delta = 2;
  const startPage = Math.max( currentPage - delta, 1 );
  const endPage = Math.min( currentPage + delta, totalPages );

  return (
    <div className="flex-center px-2 py-1 gap-2 mt-4">
      {currentPage !== 1 && (
        <Button
          color="bg-white"
          roundedRadius='rounded-full'
          height='h-full'
          shadow='shadow-md'
          colorHover="hover:bg-orange-100"
          hoverBorderColor="hover:border-orange-600"
          icon={<ArrowLongIcon className="rotate-180 text-orange-800" />}
          onClick={handlePrevPage}
        />
      )}

      <div className='flex-center h-full rounded-full bg-white px-2 py-2 shadow-md gap-2'>
        {startPage > 1 && (
          <>
            <Button
              color="bg-transparent"
              height='h-10'
              roundedRadius='rounded-full'
              shadow='shadow-none'
              colorHover="hover:bg-orange-100"
              padding='p-2'
              hoverBorderColor="hover:border-orange-600"
              textColorHover='hover:text-orange-800'
              label='1'
              onClick={() => {return onPageChange( 1 );}}
            />
            {startPage > 2 && (
              <input
                type="number"
                min={1}
                max={totalPages}
                placeholder="..."
                onChange={handlePageInputChange}
                onKeyDown={handleKeyDown}
                className="border-2 focus:border-orange-600 rounded-full px-2 py-1 h-10 aspect-square border-transparent bg-transparent outline-none text-orange-800 text-center"
              />
            )}
          </>
        )}

        {Array.from( { length: currentPage - startPage }, ( _, i ) => {return startPage + i;} ).map( i => {return (
          <Button
            key={i}
            height='h-10'
            color="bg-transparent"
            roundedRadius='rounded-full'
            textColorHover='hover:text-orange-800'
            shadow='shadow-none'
            colorHover="hover:bg-orange-100"
            hoverBorderColor="hover:border-orange-600"
            label={i.toString()}
            onClick={() => {return onPageChange( i );}}
          />
        );} )}

        <Button
          key={currentPage}
          height='h-10'
          color="bg-orange-400"
          roundedRadius='rounded-full'
          shadow='shadow-md'
          textColor='text-white'
          textColorHover='hover:text-orange-800'
          colorHover="hover:bg-orange-100"
          hoverBorderColor="hover:border-orange-600"
          label={currentPage.toString()}
          onClick={() => {return onPageChange( currentPage );}}
        />

        {Array.from( { length: endPage - currentPage }, ( _, i ) => {return currentPage + 1 + i;} ).map( i => {return (
          <Button
            key={i}
            height='h-10'
            color="bg-transparent"
            roundedRadius='rounded-full'
            textColorHover='hover:text-orange-800'
            shadow='shadow-none'
            colorHover="hover:bg-orange-100"
            hoverBorderColor="hover:border-orange-600"
            label={i.toString()}
            onClick={() => {return onPageChange( i );}}
          />
        );} )}

        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && (
              <input
                type="number"
                min={1}
                max={totalPages}
                placeholder="..."
                onChange={handlePageInputChange}
                onKeyDown={handleKeyDown}
                className="border-2 focus:border-orange-600 rounded-full px-2 py-1 h-10 aspect-square border-transparent bg-transparent outline-none text-orange-800 text-center"
              />
            )}
            <Button
              key={totalPages}
              color="bg-transparent"
              roundedRadius='rounded-full'
              shadow='shadow-none'
              textColor='text-orange-800'
              height='h-10'
              textColorHover='hover:text-orange-800'
              colorHover="hover:bg-orange-100"
              hoverBorderColor="hover:border-orange-600"
              label={totalPages.toString()}
              onClick={() => {return onPageChange( totalPages );}}
            />
          </>
        )}
      </div>

      {currentPage !== totalPages && (
        <Button
          color="bg-white"
          roundedRadius='rounded-full'
          shadow='shadow-md'
          height='h-full'
          colorHover="hover:bg-orange-100"
          hoverBorderColor="hover:border-orange-600"
          icon={<ArrowLongIcon className="text-orange-800" />}
          onClick={handleNextPage}
        />
      )}
    </div>
  );
};

export default Pagination;
